import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { 
  FileDown, 
  Filter, 
  RefreshCw, 
  TrendingUp, 
  CheckCircle2, 
  XCircle, 
  ShieldAlert,
  FileSpreadsheet,
  FileText,
  Calendar,
  Building2,
  Pill,
  AlertTriangle,
  Clock,
  MapPin
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReportsPage = () => {
  const { t, isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    location_id: '',
    dose_mg: '',
    receipt_status: ''
  });

  const [locations, setLocations] = useState([]);

  useEffect(() => {
    fetchReport();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/reports`, filters);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error(isRTL ? 'خطأ في جلب التقرير' : 'Error fetching report');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      location_id: '',
      dose_mg: '',
      receipt_status: ''
    });
  };

  const handleExport = async (type) => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.location_id && filters.location_id !== 'all') params.append('location_id', filters.location_id);
      
      const response = await axios.get(`${API}/export/${type}?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `medication_report.${type === 'excel' ? 'xlsx' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(isRTL ? 'تم التصدير بنجاح' : 'Export successful');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(isRTL ? 'خطأ في التصدير' : 'Export error');
    }
  };

  const pieData = reportData ? [
    { name: t('received'), value: reportData.stats.received, color: '#10B981' },
    { name: t('not_received'), value: reportData.stats.not_received, color: '#EF4444' },
    { name: t('preparing'), value: reportData.stats.preparing || 0, color: '#F59E0B' },
  ] : [];

  // Group by receiving area for bar chart
  const locationData = reportData?.receipts ? 
    Object.entries(
      reportData.receipts.reduce((acc, r) => {
        const locName = locations.find(l => l.id === r.location_id)?.name || r.hospital_name || 'Unknown';
        acc[locName] = (acc[locName] || 0) + 1;
        return acc;
      }, {})
    ).map(([area, count]) => ({ area, count })).slice(0, 10) : [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {t('reports_title')}
          </h1>
          <p className="text-slate-500">
            {isRTL ? 'عرض وتصدير التقارير الإدارية' : 'View and export administrative reports'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => handleExport('excel')} className="gap-2">
            <FileSpreadsheet className="h-4 w-4" />
            Excel
          </Button>
          <Button variant="outline" onClick={() => handleExport('pdf')} className="gap-2">
            <FileText className="h-4 w-4" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="animate-fade-in stagger-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5 text-sky-500" />
            {isRTL ? 'تصفية النتائج' : 'Filter Results'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>{t('start_date')}</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('end_date')}</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('filter_by_receiving_area')}</Label>
              <Select
                value={filters.location_id}
                onValueChange={(value) => handleFilterChange('location_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t('dose_mg')}</Label>
              <Input
                type="number"
                placeholder={isRTL ? 'عدد الحبات' : 'Tablets'}
                value={filters.dose_mg}
                onChange={(e) => handleFilterChange('dose_mg', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t('receipt_status')}</Label>
              <Select
                value={filters.receipt_status}
                onValueChange={(value) => handleFilterChange('receipt_status', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={isRTL ? 'الكل' : 'All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? 'الكل' : 'All'}</SelectItem>
                  <SelectItem value="received">{t('received')}</SelectItem>
                  <SelectItem value="not_received">{t('not_received')}</SelectItem>
                  <SelectItem value="preparing">{t('preparing')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button onClick={fetchReport} className="gap-2 bg-sky-500 hover:bg-sky-600">
              <RefreshCw className="h-4 w-4" />
              {t('apply_filters')}
            </Button>
            <Button variant="outline" onClick={resetFilters}>
              {t('reset_filters')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-4 border-sky-500/30 border-t-sky-500 rounded-full animate-spin" />
        </div>
      ) : reportData && (
        <>
          {/* Stats Summary */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 animate-fade-in stagger-2">
            <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-indigo-600">{t('total_records')}</p>
                    <p className="text-3xl font-bold text-indigo-700">{reportData.stats.total}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-indigo-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-emerald-600">{t('total_received')}</p>
                    <p className="text-3xl font-bold text-emerald-700">{reportData.stats.received}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">{t('total_not_received')}</p>
                    <p className="text-3xl font-bold text-red-700">{reportData.stats.not_received}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">{t('total_preparing')}</p>
                    <p className="text-3xl font-bold text-amber-700">{reportData.stats.preparing || 0}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-rose-50 to-white border-rose-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-rose-600">{t('total_allergy_prevented')}</p>
                    <p className="text-3xl font-bold text-rose-700">{reportData.stats.allergy_prevented}</p>
                  </div>
                  <ShieldAlert className="h-8 w-8 text-rose-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in stagger-3">
            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {isRTL ? 'توزيع حالات الاستلام' : 'Receipt Status Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={5}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-sky-500" />
                  {t('filter_by_receiving_area')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72">
                  {locationData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={locationData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" />
                        <YAxis 
                          dataKey="area" 
                          type="category" 
                          width={100} 
                          stroke="#64748b"
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip />
                        <Bar dataKey="count" fill="#0EA5E9" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      {t('no_data')}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Data Table */}
          <Card className="animate-fade-in stagger-4">
            <CardHeader>
              <CardTitle className="text-lg">
                {isRTL ? 'تفاصيل السجلات' : 'Record Details'}
                <span className="text-sm font-normal text-slate-500 ms-2">
                  ({reportData.receipts.length} {isRTL ? 'سجل' : 'records'})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('patient_name')}</TableHead>
                      <TableHead>{t('patient_id_number')}</TableHead>
                      <TableHead>{t('receipt_date')}</TableHead>
                      <TableHead>{t('receiving_area')}</TableHead>
                      <TableHead>{t('dose_mg')}</TableHead>
                      <TableHead>{t('receipt_status')}</TableHead>
                      <TableHead>{t('has_allergy')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.receipts.slice(0, 50).map((receipt, index) => (
                      <TableRow key={receipt.id || index}>
                        <TableCell className="font-medium">{receipt.patient_name}</TableCell>
                        <TableCell className="font-mono text-sm">{receipt.patient_id_number}</TableCell>
                        <TableCell>{receipt.receipt_date}</TableCell>
                        <TableCell>
                          {locations.find(l => l.id === receipt.location_id)?.name || receipt.hospital_name || '-'}
                        </TableCell>
                        <TableCell>{receipt.dose_mg || 0} {isRTL ? 'حبة' : 'Tablet'}</TableCell>
                        <TableCell>
                          <Badge className={
                            receipt.receipt_status === 'received' 
                              ? 'bg-emerald-500' 
                              : receipt.receipt_status === 'preparing'
                              ? 'bg-amber-500'
                              : 'bg-red-500'
                          }>
                            {receipt.receipt_status === 'received' 
                              ? t('received') 
                              : receipt.receipt_status === 'preparing'
                              ? t('preparing')
                              : t('not_received')
                            }
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {receipt.has_allergy ? (
                            <Badge className="bg-amber-500">{t('has_allergy_yes')}</Badge>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {reportData.receipts.length > 50 && (
                <p className="text-sm text-slate-500 text-center mt-4">
                  {isRTL ? `يظهر أول 50 سجل من ${reportData.receipts.length}` : `Showing first 50 of ${reportData.receipts.length} records`}
                </p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ReportsPage;
